/**
 * User Settings V2 page shell — self-edit ("/user") and admin-edit
 * (customer/plant/support user-detail routes) both mount here. Loads
 * the sectioned `/user-settings/` document, normalizes it into Formik
 * initial values, and renders the section list; each section's own
 * fields and save behavior live under `sections/user/`.
 *
 * Contains:
 * - UserSettingsViewV2 (default export)
 */

import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { useUserSettings } from '@sophocles/features/userSettings';
import { setLanguage } from '@sophocles/i18n';
import { SettingsForm, SettingsPageShell } from '@sophocles/views/settings/components';

import AccessControlSection from './sections/user/AccessControlSection';
import AdminUserHeader from './sections/user/AdminUserHeader';
import AuthenticationSection from './sections/user/AuthenticationSection';
import CommunicationSection from './sections/user/CommunicationSection';
import NotificationsSection from './sections/user/NotificationsSection';
import PersonalSection from './sections/user/PersonalSection';
import UserDangerZone from './sections/user/UserDangerZone';

// Yup schema — matches the sectioned shape returned by
// `/api/odl-app/user-settings/me/`.  Each section optional so a
// PATCH with only one section validates cleanly.
const validationSchema = yup.object({
  profile: yup
    .object({
      first_name: yup.string().nullable(),
      last_name: yup.string().nullable(),
      email: yup.string().email().nullable(),
    })
    .nullable(),
  localization: yup.object().nullable(),
  notifications: yup
    .object({
      alarm_report_email_hour: yup.number().min(0).max(23).nullable(),
    })
    .nullable(),
  contact: yup
    .object({
      email: yup.array().of(yup.string().email()).nullable(),
      sms: yup.array().of(yup.string()).nullable(),
    })
    .nullable(),
});

export default function UserSettingsViewV2() {
  const { t } = useTranslation();
  // Routes that mount this page:
  //   /user                            → self-edit (`/me/` endpoint)
  //   /user/:section                   → self-edit (legacy bookmark, param ignored)
  //   /c/:cust_id/users/:user_id       → admin-edit a user inside a customer
  //   /support/users/:user_id          → admin-edit from the support grid
  // `useParams().user_id` is the explicit signal — when present we
  // run in admin mode (target user != requester) and the consolidated
  // endpoint will return the `permissions` and `access` sections in
  // the response if the requester is allowed to see them.
  const params = useParams();
  const routeUserId = params.user_id ? Number(params.user_id) : null;
  const { user: authUser, permissions: requesterPermissions } = useUser();
  const authUserId = authUser?.get?.('id') ?? null;
  const isAdminMode = routeUserId != null && routeUserId !== authUserId;
  const { settings, status, save, reload } = useUserSettings({
    userId: isAdminMode ? routeUserId : null,
  });
  // The user whose avatar this page edits: the route user in admin mode,
  // otherwise the signed-in user.
  const avatarUserId = isAdminMode ? routeUserId : authUserId;

  // Drive the AppNav LeftNav's settings-mode rendering: when this
  // page mounts, the LeftNav swaps its global app nav for a
  // page-scoped section list (Personal / Communication / etc).
  // `scroll: true` tells LeftNav to scroll-jump on click instead
  // of route-navigating — this page is a single scrollable form
  // with all sections rendered, not separate routes per section.
  //
  // Settings sections only render when the LeftNav is expanded, so
  // force-expand on mount via the `sidebarCollapsedSet` custom
  // event (deferred one tick so AppNav's listener is registered
  // before we fire — child useEffect fires before parent's).  This
  // is unique to User Settings: other settings pages are navigated
  // to from list pages where the sidebar is already in settings
  // mode, but User Settings is reachable directly from the top-bar
  // avatar menu, so the sidebar may still be collapsed on mount.
  //
  // AppNav.js's LeftNav owns `isCollapsed` as local component state
  // (no store flag exists for it), so this window CustomEvent is the
  // only channel available for a sibling route to reach it.
  React.useEffect(() => {
    const id = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sidebarCollapsedSet', { detail: false }));
    }, 0);
    return () => clearTimeout(id);
  }, []);

  // Settings rail entries: base sections plus admin-only entries
  // when the backend includes them in the response.  The serializer
  // omits permissions/access on /me/ — their presence here drives
  // the rail items, so the rail stays in sync with the rendered
  // sections without a parallel mode flag.
  const hasPermissions = !!settings?.permissions;
  const hasAccess = !!settings?.access;
  const hasDangerZone = hasPermissions && requesterPermissions.canDeleteUsers;
  const sections = React.useMemo(() => {
    const list = [
      { id: 'settings-personal', title: t('userSettings.personal', 'Personal'), icon: 'person' },
      {
        id: 'settings-authentication',
        title: t('userSettings.v2.authentication', 'Authentication'),
        icon: 'lock',
      },
      {
        id: 'settings-communication',
        title: t('userSettings.v2.communication', 'Communication'),
        icon: 'contactMail',
      },
      {
        id: 'settings-notifications',
        title: t('userSettings.v2.notifications', 'Notifications'),
        icon: 'notifications',
      },
    ];
    // Permissions + AccessControl are rendered as a single
    // combined "Access Control" section, so the rail gets one
    // entry — driven by the presence of either response key.
    if (hasPermissions || hasAccess) {
      list.push({
        id: 'settings-access',
        title: t('userSettings.accessControl', 'Access Control'),
        icon: 'adminPanelSettings',
        pillText: t('userSettings.adminOnly', 'Admin Only'),
      });
    }
    return list;
  }, [t, hasPermissions, hasAccess]);

  if (!settings || status === 'loading') {
    return <LoadingScreen />;
  }

  // A deleted account is frozen: its parked email, profile and grants stay
  // exactly as deletion left them until Restore brings it back, so every
  // surface except the Danger Zone is read-only.
  const isDeleted = !!settings.permissions?.is_deleted;

  // Form state mirrors the server shape for read-aligned sections
  // and converts the admin-only sections to their write shape — the
  // backend returns `permissions.customer` / `permissions.groups`
  // (objects for label rendering) but accepts `customer_id` /
  // `group_ids` on PATCH; same split for `access.site_groups` →
  // `site_group_ids`.  Normalizing here keeps the Formik values
  // 1:1 with the eventual PATCH body.
  const initialValues = {
    ...settings,
    contact: {
      email: settings.contact?.email || [],
      sms: settings.contact?.sms || [],
    },
    ...(settings.permissions
      ? {
          permissions: {
            is_staff: !!settings.permissions.is_staff,
            is_superuser: !!settings.permissions.is_superuser,
            all_sitegroups_user: !!settings.permissions.all_sitegroups_user,
            customer_id: settings.permissions.customer?.id ?? null,
            group_ids: (settings.permissions.groups || []).map(g => g.id),
          },
        }
      : {}),
    ...(settings.access
      ? {
          access: {
            site_group_ids: (settings.access.site_groups || []).map(sg => sg.id),
          },
        }
      : {}),
  };

  // SMS is being retired.  Only legacy users (those with at least
  // one SMS number on file at load time) see SMS UI — they get a
  // deprecation warning and the ability to remove existing numbers
  // but cannot enroll new ones.  Everyone else sees no SMS surface.
  const hasLegacySms = (settings.contact?.sms || []).length > 0;

  const handleSave = async diff => {
    const result = await save(diff);
    // Apply a locale change to the running app — i18n otherwise only
    // picks it up from the auth payload on next login. Self-edit only;
    // an admin editing another user must not switch their own UI.
    if (!isAdminMode && diff.localization?.locale) {
      setLanguage(diff.localization.locale);
    }
    return result;
  };

  // In admin mode, the title bar reads the target user's name from
  // the loaded settings (the serializer always returns
  // profile.first_name / last_name) so we don't need a separate
  // user fetch.  Self-edit keeps the generic "User Settings" label.
  const pageTitle = isAdminMode
    ? [settings.profile?.first_name, settings.profile?.last_name].filter(Boolean).join(' ') ||
      t('userSettings.editUser', 'Edit User')
    : t('userSettings.title', 'User Settings');

  // Base path for the settings rail's URL mirroring — must be the real
  // mounted path so section links resolve. Admin-edit is reachable from
  // three mounts (support, customer-scoped, plant-scoped); self-edit is
  // /user. The rail appends `/settings/<slug>` to this.
  let settingsBasePath = '/user';
  if (isAdminMode && routeUserId) {
    if (params.cust_id && params.sitegroup_id) {
      settingsBasePath = `/c/${params.cust_id}/plant/${params.sitegroup_id}/users/${routeUserId}`;
    } else if (params.cust_id) {
      settingsBasePath = `/c/${params.cust_id}/users/${routeUserId}`;
    } else {
      settingsBasePath = `/support/users/${routeUserId}`;
    }
  }

  return (
    <SettingsPageShell
      page="user_settings"
      title={pageTitle}
      showBackButton
      contextName="user"
      basePath={settingsBasePath}
      sections={sections}
      sectionParam={params.section}
      viewOnly={isDeleted}
      viewOnlyMessage={t(
        'userSettings.v2.deletedFrozen',
        'This account is deleted and cannot be changed. Restore it from the Danger Zone first.',
      )}
    >
      {settings.permissions ? <AdminUserHeader targetUserId={routeUserId} permissions={settings.permissions} /> : null}
      <SettingsForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSave={handleSave}
        readOnly={isDeleted}
      >
        <Stack component="fieldset" disabled={isDeleted} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <PersonalSection
            avatarUserId={avatarUserId}
            profile={settings.profile}
            isAdminMode={isAdminMode && !isDeleted}
            onAvatarChanged={reload}
          />
        </Stack>

        {hasDangerZone ? (
          <UserDangerZone
            userId={routeUserId}
            permissions={settings.permissions}
            email={settings.profile?.email}
            onChanged={reload}
          />
        ) : null}

        <Stack component="fieldset" disabled={isDeleted} spacing={3} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <AuthenticationSection targetUserId={routeUserId} />

          <CommunicationSection hasLegacySms={hasLegacySms} />

          <NotificationsSection hasLegacySms={hasLegacySms} />

          {settings.permissions ? (
            <AccessControlSection
              permissions={settings.permissions}
              access={settings.access || {}}
              targetUserId={routeUserId}
            />
          ) : null}
        </Stack>
      </SettingsForm>
    </SettingsPageShell>
  );
}
